import clsx from "clsx";
import { Children, isValidElement, type HTMLAttributes, type LabelHTMLAttributes, type ReactNode } from "react";
import styles from "./SetupList.module.scss";

export interface SetupListProps extends Omit<HTMLAttributes<HTMLElement>, "title"> {
  title: string;
  /** Keeps the title for assistive technology (it names the list) without showing it. */
  hideTitle?: boolean;
  description?: ReactNode;
  children: ReactNode;
}

function SetupListRoot({ title, hideTitle = false, description, children, className, ...props }: SetupListProps) {
  return <section className={clsx(styles.root, className)} aria-label={title} {...props}>
    <h4 className={hideTitle ? styles.visuallyHidden : styles.heading}>{title}</h4>
    {description ? <p className={styles.description}>{description}</p> : null}
    <ul className={styles.list}>{children}</ul>
  </section>;
}

/** A leading control, such as a checkbox, beside the item's title. */
function SetupListItemControl({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div data-slot="setup-list-item-control" className={clsx(styles.itemControl, className)} {...props} />;
}

export interface SetupListItemTitleProps extends LabelHTMLAttributes<HTMLElement> {
  /** The id of the item's control; the title then labels it. */
  htmlFor?: string;
}

function SetupListItemTitle({ htmlFor, className, ...props }: SetupListItemTitleProps) {
  const shared = { "data-slot": "setup-list-item-title", className: clsx(styles.itemTitle, className), ...props };
  return htmlFor ? <label htmlFor={htmlFor} {...shared} /> : <span {...shared} />;
}

function SetupListItemDescription({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div data-slot="setup-list-item-description" className={clsx(styles.itemDescription, className)} {...props} />;
}

/** The item's state, such as a chip, shown at the end of the row. */
function SetupListItemStatus({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div data-slot="setup-list-item-status" className={clsx(styles.itemStatus, className)} {...props} />;
}

/** What to do about the item, such as a button, shown at the end of the row after its status. */
function SetupListItemAction({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div data-slot="setup-list-item-action" className={clsx(styles.itemAction, className)} {...props} />;
}

export interface SetupListItemProps extends HTMLAttributes<HTMLLIElement> {
  /**
   * The item's parts: `Control`, `Title`, `Description`, `Status` and `Action`, plus any other content, which runs
   * under the title. Parts are found among the direct children, so don't wrap them in a fragment or another element.
   */
  children: ReactNode;
}

function SetupListItem({ children, className, ...props }: SetupListItemProps) {
  const control: ReactNode[] = [];
  const main: ReactNode[] = [];
  const aside: ReactNode[] = [];
  Children.forEach(children, (child) => {
    if (isValidElement(child) && child.type === SetupListItemControl) control.push(child);
    else if (isValidElement(child) && (child.type === SetupListItemStatus || child.type === SetupListItemAction)) aside.push(child);
    else main.push(child);
  });
  return <li data-slot="setup-list-item" data-has-control={control.length > 0 || undefined} className={clsx(styles.item, className)} {...props}>
    {control.length ? <div className={styles.itemLeading}>{control}</div> : null}
    <div className={styles.itemMain}>{main}</div>
    {aside.length ? <div className={styles.itemAside}>{aside}</div> : null}
  </li>;
}

const Item = Object.assign(SetupListItem, {
  Control: SetupListItemControl,
  Title: SetupListItemTitle,
  Description: SetupListItemDescription,
  Status: SetupListItemStatus,
  Action: SetupListItemAction,
});

const SetupList = Object.assign(SetupListRoot, { Item });
export default SetupList;
